import { useCallback, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useWorkspaces } from '@/hooks/useWorkspaces'
import type { Workspace } from '@/api/endpoints/workspaces'

/** The value a filter carries when the user wants every workspace they may see. */
export const ALL_WORKSPACES = '' as const

/**
 * One answer to "which workspace am I looking at", shared by every page.
 *
 * Leads, Contacts and Organizations each held their own useState, so moving
 * between them silently reset the filter and the three could disagree. The
 * selection lives in the URL instead: it survives navigation and a reload,
 * it is shareable, and it dies with the tab — which is exactly the lifetime we
 * want, since nothing about one user's choice should outlive their session or
 * reach the next person to log in on this machine. Deliberately NOT persisted
 * to localStorage for that reason.
 *
 * The options are every workspace in the TENANT. GET /workspaces is not scoped
 * to membership, and deliberately so: a workspace is a place work belongs, not
 * a club you join, and a rep works across all of them. Scoping the selector to
 * membership made it lie about what was reachable — the backend would serve
 * workspace B's records happily while the picker refused to offer B.
 *
 * So membership is NOT the boundary here and this list is not an access
 * decision. The tenant is the boundary, enforced server-side on every request;
 * WHOSE rows come back is a separate question answered by the visibility rules
 * (owner/assignee for the private modules, everyone for the shared ones).
 */
export function useWorkspaceSelection() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { workspaces, activeWorkspaces, isLoading } = useWorkspaces()

  const raw = searchParams.get('workspace') ?? ALL_WORKSPACES

  // An id in the URL that is not in the authorized list is treated as "all",
  // not passed through. A stale bookmark, a link from someone with different
  // membership, or a hand-typed id therefore degrades to a view the user is
  // allowed to have instead of a request the server will reject.
  const selected = useMemo(() => {
    if (!raw) return ALL_WORKSPACES
    if (isLoading) return raw // options not in yet; do not discard the intent
    return workspaces.some((w: Workspace) => w.id === raw) ? raw : ALL_WORKSPACES
  }, [raw, workspaces, isLoading])

  // Once the authorized list has arrived and the URL still carries an id that is
  // not in it, scrub the URL. Leaving it there means the address bar claims one
  // workspace while the picker shows "All Workspaces", and every reload re-fires
  // a request the server will refuse. Clearing it makes the two agree and stops
  // the 404 repeating.
  useEffect(() => {
    if (isLoading || !raw) return
    if (workspaces.some((w: Workspace) => w.id === raw)) return
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev)
        p.delete('workspace')
        return p
      },
      { replace: true },
    )
  }, [raw, workspaces, isLoading, setSearchParams])

  const setSelected = useCallback(
    (next: string) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev)
          if (next) p.set('workspace', next)
          else p.delete('workspace')
          return p
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  /**
   * Where a NEW record should go.
   *
   * With exactly one workspace available there is nothing to ask: that one is
   * the answer. With several, the user has to say. "All Workspaces" is a
   * reading position, never a destination — resolving it to the internal
   * workspace is how partner data used to end up filed under AR Tech Solutions.
   */
  const writeTarget = useMemo(() => {
    if (activeWorkspaces.length === 1) return activeWorkspaces[0].id
    if (selected && activeWorkspaces.some((w: Workspace) => w.id === selected)) return selected
    return ''
  }, [activeWorkspaces, selected])

  return {
    workspaces,
    activeWorkspaces,
    /** '' means "every workspace I may see". */
    selected,
    setSelected,
    /** True when the account has more than one place a record could land. */
    mustChooseWorkspace: activeWorkspaces.length > 1,
    /** '' when the user still has to choose; never a silent default. */
    writeTarget,
    /** No workspace can accept a write — creation has to be blocked, not guessed. */
    hasNoWritableWorkspace: !isLoading && activeWorkspaces.length === 0,
    isLoading,
  }
}


/**
 * Build a nav href that keeps the workspace the user is currently looking at.
 *
 * Putting the selection in the URL made it survive a reload, but NOT a click on
 * the sidebar: those links point at a bare path, so moving from Tasks to
 * Activities silently dropped the workspace and the next page loaded as All
 * Workspaces. The user did not change context — the link threw it away.
 *
 * This reads the SAME `workspace` search param that `useWorkspaceSelection`
 * owns and writes. It deliberately does not hold state of its own, and there is
 * no second store to fall out of sync: one parameter, one meaning, two readers.
 *
 * Deliberately raw rather than routed through `selected`: this hook renders in
 * the sidebar on every page, and `selected` waits on the workspaces list, which
 * would make links briefly drop the param while that request is in flight. An
 * id the user may not use is not a risk here — the owning page scrubs a foreign
 * id from the URL on mount, and the server authorizes every request regardless.
 *
 * Returns identity for "All Workspaces", so no empty `?workspace=` is appended.
 */
export function useWorkspaceLink() {
  const [searchParams] = useSearchParams()
  const current = searchParams.get('workspace') ?? ALL_WORKSPACES

  return useCallback(
    (path: string) => {
      if (!current) return path
      // Preserve anything the caller already put in the path.
      const [base, existing] = path.split('?')
      const p = new URLSearchParams(existing)
      p.set('workspace', current)
      return `${base}?${p.toString()}`
    },
    [current],
  )
}
